import { createElement } from './shared/helpers';

class ImageZoom {
    private lastFocusable: HTMLElement;

    private imageContainer: HTMLElement;

    private loadingSpinner: HTMLElement;

    private dialogElement: HTMLElement;

    private dialogId = 'zoomoverlay';

    constructor(elements: NodeListOf<HTMLElement>) {
        if (!elements) return;

        this.prepareStructure();
        const zoomtriggers = Array.from(elements);

        if (zoomtriggers.length) {
            zoomtriggers.forEach((el: HTMLElement) => {
                el.addEventListener('click', this.openImage.bind(this));
            });
        }
    }

    public prepareStructure() {
        const container = createElement('dialog', {
            class: 'eika-image-zoom',
            id: this.dialogId,
            'aria-modal': 'true',
            'aria-labelledby': 'zoomoverlayTitle',
            inert: 'true',
        });

        const label = createElement('span', {
            class: 'screen-reader-text',
            id: 'zoomoverlayTitle',
        });

        const inner = createElement('div', {
            class: 'eika-image-zoom__container',
        });

        const button = createElement('button', {
            class: 'eika-image-zoom__close',
            id: 'zoomoverlayCloseButton',
        });
        const buttonIconWrapper = createElement('span', { class: 'sgw-icon' });
        const buttonIconInner = createElement('span', { class: 'icon icon--24 icon--kryss-24' });
        buttonIconWrapper.appendChild(buttonIconInner);
        button.appendChild(buttonIconWrapper);

        const backdrop = createElement('div', {
            class: 'eika-image-zoom__backdrop',
            'aria-hidden': 'true',
        });

        const loading = createElement('span', {
            class: 'eika-image-zoom__loading',
            id: 'loadingImage',
        });
        const loadingIconWrapper = createElement('span', { class: 'sgw-icon' });
        const loadingIconInner = createElement('span', { class: 'icon icon--36 icon--stjernedryss-36' });
        loadingIconWrapper.appendChild(loadingIconInner);
        loading.appendChild(loadingIconWrapper);

        loading.style.display = 'none';
        this.loadingSpinner = loading;

        inner.appendChild(loading);
        button.addEventListener('click', this.closeImage.bind(this));

        container.appendChild(label);
        container.appendChild(button);
        container.appendChild(inner);
        container.appendChild(backdrop);

        this.imageContainer = inner;
        this.lastFocusable = document.body;

        document.addEventListener('keydown', this.closeOnEsc.bind(this));
        container.addEventListener('keydown', this.focusTrap.bind(this));

        document.body.appendChild(container);
    }

    private openImage(event: MouseEvent) {
        event.preventDefault();
        const link = event.currentTarget as HTMLAnchorElement;

        if (!link || !this.imageContainer) return;

        this.dialogElement = document.getElementById(this.dialogId) as HTMLDialogElement;
        this.dialogElement?.removeAttribute('inert');

        this.lastFocusable = link;
        this.isLoading(true);

        const label = document.querySelector('#zoomoverlayTitle') as HTMLSpanElement;
        if (label instanceof HTMLSpanElement) {
            label.innerText = link.getAttribute('data-label') ?? '';
        }
        const btn = document.querySelector('#zoomoverlayCloseButton') as HTMLButtonElement | null;
        btn?.setAttribute('aria-label', link.getAttribute('data-close') ?? '');
        btn?.focus();

        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.paddingRight = `${scrollbarWidth}px`;
        document.body.classList.add('image-zoom-overlay-visible');

        const src = link.getAttribute('href');
        if (!src) return;

        const image = new Image();
        image.src = src;

        image.onload = () => {
            const newImg = document.createElement('img');
            newImg.src = image.src;
            newImg.alt = link.getAttribute('data-label') ?? '';
            this.imageContainer.appendChild(newImg);
            this.isLoading(false);
        };
    }

    private closeImage() {
        if (!this.imageContainer) return;

        if (document.body.classList.contains('image-zoom-overlay-visible')) {
            document.body.classList.remove('image-zoom-overlay-visible');
            document.body.style.paddingRight = '';

            this.dialogElement?.setAttribute('inert', 'true');

            const img = this.imageContainer.querySelector('img');

            img?.remove();
            this.lastFocusable?.focus();
        }
    }

    private isLoading(show: boolean) {
        if (!this.loadingSpinner) return;
        this.loadingSpinner.style.display = show ? 'block' : 'none';
    }

    private closeOnEsc(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            this.closeImage();
        }
    }

    private focusTrap(event: KeyboardEvent) {
        const overlay = document.querySelector('#zoomoverlay');
        if (!overlay) return;

        const focusable = Array.from(overlay.querySelectorAll('button,[href],[tabindex]:not([tabindex="-1"]') ?? []);
        if (focusable.length) {
            if (event.key === 'Tab') {
                const firstEl = focusable[0] as HTMLElement;
                const lastEl = focusable[focusable.length - 1] as HTMLElement;

                if (event.shiftKey && document.activeElement === firstEl) {
                    event.preventDefault();
                    lastEl.focus();
                } else if (!event.shiftKey && document.activeElement === lastEl) {
                    event.preventDefault();
                    firstEl.focus();
                }
            }
        }
    }
}

export default function eikaImageZoom() {
    const zoomableContent = document.querySelectorAll<HTMLElement>('.sgw-image-card-zoom__button');

    if (zoomableContent) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const ImageZoomInstance = new ImageZoom(zoomableContent);
    }
}
